import { Box } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
	type SearchOptions,
	searchCourseMindItems as searchCourseMindItemsAction,
} from '../../../actions/search.actions';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import NotificationAlert from '../../../components/NotificationAlert';
import type { Alert } from '../../../components/NotificationAlert/NotificationAlert';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { SearchFiltersContainer } from '../../../components/Search/SearchFiltersContainer';
import SearchResults, {
	type SearchResultsItemsTab,
} from '../../../components/Search/SearchResults/SearchResults';
import { zIndex } from '../../../constants';
import {
	getPaginationSelector,
	selectCourseSearchFilters,
} from '../../../reducers/search.selectors';
import services from '../../../services';
import { trackingEvents } from '../../../services/tracking/trackConfig';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import { type SearchFilters, SearchResultsTab } from '../../../types/search';
import type { SearchEventData } from '../../../utils/ampli';
import queryToSearchFilters from '../../../utils/queryToSearchFilters';
import useAction from '../../../utils/useAction';
import useCardDimensions from '../../../utils/useCardDimensions';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import { SearchPageContainer } from '../../SearchPage/SearchPageContainer';

export const useNewUiSearchPagePadding = () => {
	const { isXsScreen, isSmScreen } = useScreenSize();

	if (isXsScreen) return spaces.md;
	if (isSmScreen) return spaces.lg;
	return spaces.xl;
};

const CourseSearchPage = () => {
	const { t } = useTranslation();
	const { match } = useRouter();
	const cardDimensions = useCardDimensions();
	const { mainContentContainer } = useContext(MasterLayoutContext);

	// Computed at render time (not held in a ref reassigned inside the search effect below)
	// so the value passed to SearchResults already describes the *new* search on the render
	// that follows a URL change. SearchResults' SearchCompleted guard compares this against
	// the filters the store's pages were fetched for; a ref would still hold the previous
	// search's filters when that child effect commits, so the two would agree and the event
	// would report the previous search's total under the new search id (PRO-91).
	const requestFilters = useMemo(
		() => queryToSearchFilters(match.location.search),
		[match.location.search]
	);
	const searchCourseMindItems = useAction(searchCourseMindItemsAction);
	const courseMindItemsPagination = useSelector(getPaginationSelector('courseMindItems'));
	const lastCourseSearchFilters = useSelector(selectCourseSearchFilters);
	const { notification, hideNotification, showNotification } = useContext(NotificationContext);

	// mainContentContainer transitions null→ref once on mount (via MasterLayout's
	// ref callback). Reading it through a ref keeps the scroll-to-top behavior
	// intact without making the search effect re-run on that transition — that
	// re-run would otherwise fire a second identical /v4/mindItems/search.
	const mainContentContainerRef = useRef(mainContentContainer);
	mainContentContainerRef.current = mainContentContainer;

	const haveSearchFiltersChanged = (filters?: SearchFilters) => {
		return !_.isEqual(filters, requestFilters);
	};

	// Memoized like SearchPage's (`SearchPage.tsx:267`): as a fresh object literal it changed
	// identity on every render, and SearchResults' tracking effect takes it as a dependency —
	// so the effect re-ran on every render of this page, including renders that commit before
	// a submitted search reaches the URL. Declared above its readers so a future direct call
	// during render can't hit the temporal dead zone.
	const eventPayload: { other: SearchEventData } = useMemo(
		() => ({
			other: {
				tab: SearchResultsTab.COURSES,
				results: courseMindItemsPagination.offset,
				query: match.location.query,
				term: requestFilters.term,
				appSection: 'courses',
			},
		}),
		[courseMindItemsPagination.offset, match.location.query, requestFilters.term]
	);

	const executeCourseMindItemsSearch = (options: SearchOptions) => {
		searchCourseMindItems(requestFilters, { ...options, populateProducts: true });
		services.track.eventTrack(trackingEvents.searchExecute, _.merge(eventPayload));
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const options: SearchOptions = {
			clearPreviousResults: true,
		};

		if (haveSearchFiltersChanged(lastCourseSearchFilters)) {
			mainContentContainerRef.current?.scrollTo(0, 0);
			executeCourseMindItemsSearch(options);
		}
	}, [match.location.search]);

	const tabs: SearchResultsItemsTab[] = [
		{
			key: SearchResultsTab.COURSES,
			name: t('Courses'),
			icon: 'book',
			loadMore: () => executeCourseMindItemsSearch({ clearPreviousResults: false }),
		},
	];

	const containerSx = {
		maxWidth: cardDimensions.desktop.maxWidth,
		marginX: 'auto',
		paddingX: useNewUiSearchPagePadding(),
		width: '100%',
	};

	const handleShowNotification = (alert: Alert) => {
		showNotification(alert);
	};

	const memoizedNotification = useMemo(() => {
		if (!notification) return null;
		return (
			<Box sx={{ position: 'sticky', top: 0, zIndex: zIndex.alert, width: '100%' }}>
				<Box sx={{ position: 'relative', width: '100%' }}>
					<NotificationAlert
						message={notification.message}
						severity={notification.severity}
						onClose={hideNotification}
						alertSx={{
							width: '100%',
							marginLeft: `${spaces.md} !important`,
							marginRight: `${spaces.md} !important`,
							maxWidth: cardDimensions.desktop.maxWidth,
							marginX: 'auto',
						}}
					/>
				</Box>
			</Box>
		);
	}, [notification, hideNotification, cardDimensions.desktop.maxWidth]);

	return (
		<MasterLayout forceNewNavigation>
			<SearchPageContainer>
				{memoizedNotification}
				<Box sx={containerSx}>
					<PageHeader sx={{ color: palette.text.primary }} text={t('Find your course')} />
					<SearchFiltersContainer
						requestFilters={requestFilters}
						visibleKeys={['courseTheme', 'schoolType', 'courseType', 'publisher']}
						invisibleKeys={['courseDuration', 'subject', 'classYear']}
						dialogFiltersType="course"
						dialogFiltersOrder={[
							'courseTheme',
							'schoolType',
							'courseType',
							'publisher',
							'courseDuration',
							'subject',
							'classYear',
						]}
					/>
				</Box>
				<Box marginTop="-24px">
					<SearchResults
						trackingPayload={eventPayload.other}
						requestFilters={requestFilters}
						tabs={tabs}
						currentTab={SearchResultsTab.COURSES}
						searchTerm={requestFilters.term}
						forceNewDesign
						onShowNotification={handleShowNotification}
					/>
				</Box>
			</SearchPageContainer>
		</MasterLayout>
	);
};

export default CourseSearchPage;
